import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Layout from '../../components/Layout';
import PieChartWithLegend from '../../components/PieChartWithLegend';
import NetSales from '../../components/NetSales';
import Deposits from '../../components/Deposits';
import GrossSales from '../../components/GrossSales';
import PaymentMethods from '../../components/PaymentMethods';
import TotalCollected from '../../components/TotalCollected';
import DashboardFilters from '../containers/DashboardFilters';
import GiftCardsSold from '../../components/components/GiftCardsSold';
import BarberFilter from '../../metrics/barberFilterContainer';
import DirtyFilterOverlay from '../../components/DirtyFilterOverlay';
import { RoleAllowBrand, RoleAwareShop, RoleDenyBrand } from '../../../../auth/roleAware';
import CountAndAmountBlock from '../../components/CountAndAmountBlock';
import ShopFilter from '../../filters/ShopFilter';
import { taxBreakdownArray } from '../../shapes';

const Dashboard = ({
  upcomingDeposits,
  grossSales,
  servicesCount,
  productsCount,
  paymentCash,
  paymentGiftCards,
  paymentPosCard,
  paymentSquireCard,
  showGiftCards,
  servicesTotal,
  servicesChartData,
  productsTotal,
  productsChartData,
  refunds,
  discounts,
  fees,
  netSales,
  taxes,
  tips,
  totalCollected,
  canceledAppointmentsCount,
  canceledAppointmentsAmount,
  noShowAppointmentsCount,
  grossSecuredDepositsCount,
  grossSecuredDepositsAmount,
  noShowAppointmentsAmount,
  loanRepayment,
  giftCardsSoldGross,
  giftCardsTotalCollected,
  lastDepositAmount,
  lastDepositDate,
  unchargedAppointmentsCount,
  unchargedAppointmentsAmount,
  taxBreakdown,
  grossSalesKind,
  showDepositsSection,
  showGiftCardSection,
  newMembershipCount,
  recurringMembershipCount,
  grossTotal,
  newMembershipGrossTotal,
  recurringMembershipGrossTotal,
  productGrossTotal,
  serviceGrossTotal,
  paymentDepositsEnabled,
}) => {
  const className = paymentDepositsEnabled ? 'total-sales-deposits-grid' : 'total-sales-grid';

  return (
    <Layout title="Total Gross Sales">
      <div className="c-total-sales-wrapper">
        <div className="c-report-filters u-mb-">
          <div className="u-mr0 large-and-up-mr- u-mb">
            <DashboardFilters
              showCsvExport
              csvJobName="total-sales-report"
              csvModalTitle="Total Gross Sales"
              disableBarberFilter
            >
              <RoleAllowBrand>
                <ShopFilter />
              </RoleAllowBrand>
              <RoleDenyBrand>
                <RoleAwareShop>
                  <div className="c-report-filters__item">
                    <BarberFilter />
                  </div>
                </RoleAwareShop>
              </RoleDenyBrand>
            </DashboardFilters>
          </div>
        </div>

        <div
          className={cx('u-position-relative', {
            'c-total-sales-grid': showDepositsSection && !paymentDepositsEnabled,
            'c-total-sales-deposits-grid': showDepositsSection && paymentDepositsEnabled,
            'c-total-sales-grid-brand': !showDepositsSection && !paymentDepositsEnabled,
            'c-total-sales-deposits-grid-brand': !showDepositsSection && paymentDepositsEnabled,
          })}
        >
          <DirtyFilterOverlay />
          <div className={`c-${className}__deposits`}>
            <Deposits
              display-if={showDepositsSection}
              upcomingDeposits={upcomingDeposits}
              lastDepositAmount={lastDepositAmount}
              lastDepositDate={lastDepositDate}
            />
          </div>

          <div className={`c-${className}__gross-sales`}>
            <GrossSales
              grossSales={grossSales}
              serviceSales={servicesCount}
              productSales={productsCount}
              newMembershipCount={newMembershipCount}
              recurringMembershipCount={recurringMembershipCount}
              grossTotal={grossTotal}
              newMembershipGrossTotal={newMembershipGrossTotal}
              recurringMembershipGrossTotal={recurringMembershipGrossTotal}
              productGrossTotal={productGrossTotal}
              serviceGrossTotal={serviceGrossTotal}
              taxes={taxes}
              grossSalesKind={grossSalesKind}
            />
          </div>

          <div className={`c-${className}__payment-methods`}>
            <PaymentMethods
              paymentPosCard={paymentPosCard}
              paymentSquireCard={paymentSquireCard}
              paymentCash={paymentCash}
              paymentGiftCards={paymentGiftCards}
              showGiftCards={showGiftCards}
            />
          </div>

          <div className={`c-${className}__gift-cards-sold`}>
            <GiftCardsSold
              display-if={showGiftCardSection}
              giftCardsSoldGross={giftCardsSoldGross}
              giftCardsTotalCollected={giftCardsTotalCollected}
            />
          </div>

          <div className={`c-${className}__sales-by-service`}>
            <PieChartWithLegend
              data={servicesChartData}
              total={servicesTotal}
              title="Net sales by service"
              tooltipText="services"
              automationId="netSalesByService"
            />
          </div>

          <div className={`c-${className}__sales-by-product`}>
            <PieChartWithLegend
              data={productsChartData}
              total={productsTotal}
              title="Net sales by product"
              tooltipText="products"
              automationId="netSalesByProduct"
            />
          </div>
          <div className={`c-${className}__net-sales`}>
            <NetSales totalSales={grossSales} refunds={refunds} discounts={discounts} netSales={netSales} />
          </div>
          <div className={`c-${className}__total-collected`}>
            <TotalCollected
              netSales={netSales}
              taxes={taxes}
              tips={tips}
              fees={fees}
              totalCollected={totalCollected}
              loanRepayment={loanRepayment}
              taxBreakdown={taxBreakdown}
            />
          </div>
          <div className={`c-${className}__canceled-appointments`}>
            <CountAndAmountBlock
              title="Canceled"
              count={canceledAppointmentsCount}
              amount={canceledAppointmentsAmount}
              automationIdPart="canceledAppointments"
            />
          </div>
          <div className={`c-${className}__no-shows-appointments`}>
            <CountAndAmountBlock
              title="No Shows"
              count={noShowAppointmentsCount}
              amount={noShowAppointmentsAmount}
              automationIdPart="noShowAppointments"
            />
          </div>
          {paymentDepositsEnabled && (
            <div className={`c-${className}__secured-deposits`}>
              <CountAndAmountBlock
                title="Booking Deposits"
                count={grossSecuredDepositsCount}
                amount={grossSecuredDepositsAmount}
                automationIdPart="grossSecuredDeposits"
              />
            </div>
          )}
          <div className={`c-${className}__uncharged-appointments`}>
            <CountAndAmountBlock
              title="Not Marked"
              count={unchargedAppointmentsCount}
              amount={unchargedAppointmentsAmount}
              automationIdPart="unchargedAppointments"
            />
          </div>
        </div>
      </div>
    </Layout>
  );
};

Dashboard.propTypes = {
  upcomingDeposits: PropTypes.number.isRequired,
  grossSales: PropTypes.number.isRequired,
  servicesCount: PropTypes.number.isRequired,
  productsCount: PropTypes.number.isRequired,
  paymentCash: PropTypes.number.isRequired,
  paymentGiftCards: PropTypes.number.isRequired,
  paymentPosCard: PropTypes.number.isRequired,
  paymentSquireCard: PropTypes.number.isRequired,
  showGiftCards: PropTypes.bool.isRequired,
  servicesTotal: PropTypes.number.isRequired,
  servicesChartData: PropTypes.arrayOf(PropTypes.object).isRequired,
  productsTotal: PropTypes.number.isRequired,
  productsChartData: PropTypes.arrayOf(PropTypes.object).isRequired,
  refunds: PropTypes.number.isRequired,
  discounts: PropTypes.number.isRequired,
  fees: PropTypes.number.isRequired,
  netSales: PropTypes.number.isRequired,
  taxes: PropTypes.number.isRequired,
  tips: PropTypes.number.isRequired,
  totalCollected: PropTypes.number.isRequired,
  canceledAppointmentsCount: PropTypes.number.isRequired,
  canceledAppointmentsAmount: PropTypes.number.isRequired,
  noShowAppointmentsCount: PropTypes.number.isRequired,
  noShowAppointmentsAmount: PropTypes.number.isRequired,
  loanRepayment: PropTypes.number.isRequired,
  giftCardsSoldGross: PropTypes.number.isRequired,
  giftCardsTotalCollected: PropTypes.number.isRequired,
  lastDepositAmount: PropTypes.number.isRequired,
  lastDepositDate: PropTypes.instanceOf(Date),
  unchargedAppointmentsCount: PropTypes.number.isRequired,
  unchargedAppointmentsAmount: PropTypes.number.isRequired,
  taxBreakdown: taxBreakdownArray.isRequired,
  grossSalesKind: PropTypes.string.isRequired,
  showDepositsSection: PropTypes.bool.isRequired,
  showGiftCardSection: PropTypes.bool.isRequired,
  newMembershipCount: PropTypes.number.isRequired,
  recurringMembershipCount: PropTypes.number.isRequired,
  grossTotal: PropTypes.number.isRequired,
  newMembershipGrossTotal: PropTypes.number.isRequired,
  recurringMembershipGrossTotal: PropTypes.number.isRequired,
  productGrossTotal: PropTypes.number.isRequired,
  serviceGrossTotal: PropTypes.number.isRequired,
};

Dashboard.defaultProps = {
  lastDepositDate: null,
};

export default Dashboard;
